import { type PaginationState, type SortingState } from '@tanstack/react-table'

import {
  type GetBrokersTaskWorkflowsFiltersCoverageTypesItem,
  type GetBrokersTaskWorkflowsFiltersEmployerReviewStatusesItem,
  type GetBrokersTaskWorkflowsFiltersEnrollmentEventTypesItem,
  type GetBrokersTaskWorkflowsFiltersSourceItem,
  type GetBrokersTaskWorkflowsFiltersStatusesItem,
  type GetBrokersTaskWorkflowsFiltersWorkflowTaskCategoriesItem,
  type GetBrokersTaskWorkflowsFiltersWorkflowTaskExecutionContextsItem,
  type GetBrokersTaskWorkflowsFiltersWorkflowTaskExecutionMethodsItem,
  type GetBrokersTaskWorkflowsFiltersWorkflowTaskStatusesItem,
  type GetBrokersTaskWorkflowsFiltersWorkflowTypesItem,
  type GetBrokersTaskWorkflowsParams,
} from 'utils/web_api.schemas'

export type WorkflowsSingleValueFilters = {
  after_date?: string
  before_date?: string
  deadline_date_after?: string
  deadline_date_before?: string
  has_failed_tasks?: string
}

export type WorkflowsArrayValueFilters = {
  statuses?: GetBrokersTaskWorkflowsFiltersStatusesItem[]
  workflow_types?: GetBrokersTaskWorkflowsFiltersWorkflowTypesItem[]
  enrollment_ids?: string[]
  member_ids?: string[]
  broker_ids?: string[]
  brokerage_ids?: string[]
  carrier_names?: string[]
  enrollment_categories?: string[]
  employer_ids?: string[]
  employer_review_statuses?: GetBrokersTaskWorkflowsFiltersEmployerReviewStatusesItem[]
  enrollment_event_types?: GetBrokersTaskWorkflowsFiltersEnrollmentEventTypesItem[]
  coverage_types?: GetBrokersTaskWorkflowsFiltersCoverageTypesItem[]
  source?: GetBrokersTaskWorkflowsFiltersSourceItem[]
  workflow_task_types?: string[]
  workflow_task_statuses?: GetBrokersTaskWorkflowsFiltersWorkflowTaskStatusesItem[]
  workflow_task_execution_methods?: GetBrokersTaskWorkflowsFiltersWorkflowTaskExecutionMethodsItem[]
  workflow_task_execution_contexts?: GetBrokersTaskWorkflowsFiltersWorkflowTaskExecutionContextsItem[]
  workflow_task_categories?: GetBrokersTaskWorkflowsFiltersWorkflowTaskCategoriesItem[]
}

export type WorkflowsFilters = WorkflowsSingleValueFilters &
  WorkflowsArrayValueFilters

/**
 * Helper function to transform our filter format to the Grape API params format
 */
export function transformFiltersToParams (
  filters: WorkflowsFilters,
  pagination: PaginationState,
  sorting: SortingState,
  evaluateReadiness?: boolean
): GetBrokersTaskWorkflowsParams {
  const params: GetBrokersTaskWorkflowsParams = {
    page: pagination.pageIndex,
    items: pagination.pageSize,
  }

  // Add evaluate_readiness if explicitly provided
  if (evaluateReadiness !== undefined) {
    params.evaluate_readiness = evaluateReadiness
  }

  // Add sorting if present
  if (sorting.length > 0) {
    const { id, desc } = sorting[0]
    // Map deadline_date column ID to the new API parameter name
    const sortKey =
      (id === 'deadline_date' ? 'sort[deadline_date]' : `sort[${id}]`) as keyof Pick<
        GetBrokersTaskWorkflowsParams,
        | 'sort[status]'
        | 'sort[created_at]'
        | 'sort[workflow_type]'
        | 'sort[member_name]'
        | 'sort[deadline_date]'
      >
    params[sortKey] = (desc ? 'desc' : 'asc') as any
  }

  // Add array filters
  if (filters.statuses) {
    params['filters[statuses][]'] = filters.statuses
  }
  if (filters.workflow_types) {
    params['filters[workflow_types][]'] = filters.workflow_types
  }
  if (filters.enrollment_ids) {
    params['filters[enrollment_ids][]'] = filters.enrollment_ids
  }
  if (filters.member_ids) {
    params['filters[member_ids][]'] = filters.member_ids
  }
  if (filters.broker_ids) {
    params['filters[broker_ids][]'] = filters.broker_ids
  }
  if (filters.brokerage_ids) {
    params['filters[brokerage_ids][]'] = filters.brokerage_ids
  }
  if (filters.carrier_names) {
    params['filters[carrier_names][]'] = filters.carrier_names
  }
  if (filters.enrollment_categories) {
    params['filters[enrollment_categories][]'] = filters.enrollment_categories
  }
  if (filters.employer_ids) {
    params['filters[employer_ids][]'] = filters.employer_ids
  }
  if (filters.employer_review_statuses) {
    params['filters[employer_review_statuses][]'] =
      filters.employer_review_statuses
  }
  if (filters.enrollment_event_types) {
    params['filters[enrollment_event_types][]'] =
      filters.enrollment_event_types
  }
  if (filters.coverage_types) {
    params['filters[coverage_types][]'] = filters.coverage_types
  }
  if (filters.source) {
    params['filters[source][]'] = filters.source
  }
  if (filters.workflow_task_types) {
    params['filters[workflow_task_types][]'] = filters.workflow_task_types
  }
  if (filters.workflow_task_statuses) {
    params['filters[workflow_task_statuses][]'] = filters.workflow_task_statuses
  }
  if (filters.workflow_task_execution_methods) {
    params['filters[workflow_task_execution_methods][]'] =
      filters.workflow_task_execution_methods
  }
  if (filters.workflow_task_execution_contexts) {
    params['filters[workflow_task_execution_contexts][]'] =
      filters.workflow_task_execution_contexts
  }
  if (filters.workflow_task_categories) {
    params['filters[workflow_task_categories][]'] =
      filters.workflow_task_categories
  }

  // Add single value filters
  if (filters.after_date) {
    params['filters[after_date]'] = filters.after_date
  }
  if (filters.before_date) {
    params['filters[before_date]'] = filters.before_date
  }
  if (filters.deadline_date_after) {
    params['filters[deadline_date_after]'] =
      filters.deadline_date_after
  }
  if (filters.deadline_date_before) {
    params['filters[deadline_date_before]'] =
      filters.deadline_date_before
  }
  if (filters.has_failed_tasks) {
    params['filters[has_failed_tasks]'] =
      filters.has_failed_tasks === 'true'
  }

  return params
}
